import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Users, X, RotateCcw, Trash2, EllipsisVertical, ShieldOff, MailPlus, ArrowLeftRight, UserX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { ConfirmDialog } from '@/components/custom/ConfirmDialog';
import {
  fetchAdminUsers,
  createUserApi,
  updateUserApi,
  resetUserPasswordApi,
  reset2faApi,
  resendWelcomeApi,
  convertAuthTypeApi,
  deleteUserApi,
  anonymiseUserApi,
  fetchUserDeps,
  fetchAdminGroups,
} from '@/hooks/useAdmin';
import type { UserDeps } from '@/hooks/useAdmin';
import { fetchOperators } from '@/hooks/useTickets';
import { useAuth } from '@/hooks/useAuth';
import type { AdminUserItem, CreateUserRequest, UpdateUserRequest, OperatorListItem, GroupAdminItem } from '@uniticket/shared';

const ROLES = [
  { value: 'SYSTEM_ADMIN', label: 'System Admin' },
  { value: 'UNIFIBER_OPERATOR', label: 'Unifiber Operator' },
  { value: 'EXTERNAL_OPERATOR_USER', label: 'External Operator' },
];

export function UserManagement() {
  const { t } = useTranslation('admin');
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [operators, setOperators] = useState<OperatorListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterOperator, setFilterOperator] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUserItem | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'resetPassword' | 'reset2fa' | 'resendWelcome' | 'anonymise'; user: AdminUserItem } | null>(null);
  const [convertTarget, setConvertTarget] = useState<AdminUserItem | null>(null);
  const [convertAuthType, setConvertAuthType] = useState<string>('');
  const [deleteTarget, setDeleteTarget] = useState<{ user: AdminUserItem; deps: UserDeps } | null>(null);
  const pageSize = 50;

  const loadData = useCallback(async () => {
    try {
      const params: Record<string, string> = {
        limit: String(pageSize),
        offset: String(page * pageSize),
      };
      if (search) params.search = search;
      if (filterRole) params.role = filterRole;
      if (filterOperator) params.operatorId = filterOperator;
      if (filterActive) params.active = filterActive;

      const [userRes, opRes] = await Promise.all([
        fetchAdminUsers(params),
        operators.length > 0 ? Promise.resolve({ data: operators }) : fetchOperators(),
      ]);
      setUsers(userRes.data);
      setTotal(userRes.meta.total);
      if (operators.length === 0) setOperators(opRes.data);
    } catch (err: any) {
      toast.error(err.message || t('users.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterRole, filterOperator, filterActive, operators.length, t]);

  useEffect(() => { loadData(); }, [loadData]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput); setPage(0); }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  function handleAdd() { setEditing(null); setDialogOpen(true); }
  function handleEdit(u: AdminUserItem) { setEditing(u); setDialogOpen(true); }

  async function handleSave(data: CreateUserRequest | UpdateUserRequest) {
    try {
      if (editing) {
        await updateUserApi(editing.id, data as UpdateUserRequest);
        toast.success(t('users.updated'));
      } else {
        await createUserApi(data as CreateUserRequest);
        toast.success(t('users.created'));
      }
      setDialogOpen(false);
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('users.saveError'));
    }
  }

  async function handleResetPassword(id: string) {
    try {
      const res = await resetUserPasswordApi(id);
      toast.success(res.message);
      setConfirmAction(null);
    } catch (err: any) {
      toast.error(err.message || t('users.resetError'));
    }
  }

  async function handleReset2fa(id: string) {
    try {
      const res = await reset2faApi(id);
      toast.success(res.message);
      setConfirmAction(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('users.reset2faError'));
      setConfirmAction(null);
    }
  }

  async function handleResendWelcome(id: string) {
    try {
      const res = await resendWelcomeApi(id);
      toast.success(res.message);
      setConfirmAction(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('users.resendWelcomeError'));
      setConfirmAction(null);
    }
  }

  async function handleAnonymise(id: string) {
    try {
      const res = await anonymiseUserApi(id);
      if (res.data.anonymised) {
        toast.success(t('users.anonymised'));
      } else {
        toast.info(t('users.alreadyAnonymised'));
      }
      setConfirmAction(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('users.anonymiseError'));
      setConfirmAction(null);
    }
  }

  async function handleConvertAuth() {
    if (!convertTarget || !convertAuthType) return;
    try {
      await convertAuthTypeApi(convertTarget.id, convertAuthType);
      toast.success(t('users.authConverted'));
      setConvertTarget(null);
      setConvertAuthType('');
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('users.convertError'));
    }
  }

  async function handleDeleteClick(u: AdminUserItem) {
    try {
      const res = await fetchUserDeps(u.id);
      setDeleteTarget({ user: u, deps: res.data });
    } catch (err: any) {
      toast.error(err.message || t('users.deleteError'));
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    try {
      await deleteUserApi(deleteTarget.user.id);
      toast.success(t('users.deleted'));
      setDeleteTarget(null);
      loadData();
    } catch (err: any) {
      setDeleteTarget(null);
      toast.error(err.message || t('users.deleteError'));
    }
  }

  const totalPages = Math.ceil(total / pageSize);

  if (loading && users.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">{t('users.title')}</h2>
            <p className="text-sm text-slate-500">{t('users.count', { count: total })}</p>
          </div>
        </div>
        <Button onClick={handleAdd} className="bg-brand-gradient text-white hover:opacity-90">
          <Plus className="w-4 h-4 mr-2" />
          {t('users.add')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Input
          placeholder={t('users.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-64"
        />
        <Select value={filterRole} onValueChange={(v) => { setFilterRole(v === 'ALL' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t('users.filterRole')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t('users.allRoles')}</SelectItem>
            {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterOperator} onValueChange={(v) => { setFilterOperator(v === 'ALL' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t('users.filterOperator')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t('users.allOperators')}</SelectItem>
            {operators.map((op) => <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterActive} onValueChange={(v) => { setFilterActive(v === 'ALL' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder={t('users.filterStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t('users.allStatuses')}</SelectItem>
            <SelectItem value="true">{t('users.activeOnly')}</SelectItem>
            <SelectItem value="false">{t('users.inactiveOnly')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">{t('users.cols.name')}</th>
                <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">{t('users.cols.email')}</th>
                <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">{t('users.cols.role')}</th>
                <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">{t('users.cols.operator')}</th>
                <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">{t('users.cols.groups')}</th>
                <th className="text-center px-2 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">{t('users.cols.auth')}</th>
                <th className="text-center px-2 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">{t('users.cols.status')}</th>
                <th className="text-center px-2 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">2FA</th>
                <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap">{t('users.cols.lastLogin')}</th>
                <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap sticky right-0 bg-slate-50 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">{t('users.cols.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-slate-400">
                    {t('users.empty')}
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="group/row hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-3 font-medium text-slate-700">{u.displayName}</td>
                    <td className="px-3 py-3 text-slate-600 text-xs">{u.email}</td>
                    <td className="px-3 py-3">
                      <Badge variant={u.role === 'SYSTEM_ADMIN' ? 'purple' : u.role === 'UNIFIBER_OPERATOR' ? 'blue' : 'default'}>
                        {ROLES.find((r) => r.value === u.role)?.label ?? u.role}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 text-slate-600">
                      <span className="font-mono text-xs text-slate-400 mr-1">{u.operatorCode}</span>
                      {u.operatorName}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(u.groups ?? []).map((g) => (
                          <Badge key={g.id} variant="default" className="text-[10px]">{g.name}</Badge>
                        ))}
                        {(!u.groups || u.groups.length === 0) && (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-3 text-center">
                      {u.authType === 'ENTRA_ID' && (
                        <span className="inline-flex items-center text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Microsoft</span>
                      )}
                      {u.authType === 'LOCAL' && (
                        <span className="inline-flex items-center text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">Local</span>
                      )}
                      {u.authType === 'BOTH' && (
                        <span className="inline-flex items-center text-xs font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">{t('users.authBoth')}</span>
                      )}
                    </td>
                    <td className="px-2 py-3 text-center">
                      {u.isActive ? (
                        <span className="inline-flex items-center text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Active</span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Inactive</span>
                      )}
                    </td>
                    <td className="px-2 py-3 text-center">
                      {u.totpEnabled ? (
                        <span className="inline-flex items-center text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">On</span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Off</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-slate-500 text-xs whitespace-nowrap">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-3 text-right sticky right-0 bg-white group-hover/row:bg-slate-50 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(u)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-brand-rose hover:bg-brand-rose/5 transition-colors"
                          title={t('users.editUser')}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                              <EllipsisVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {u.authType !== 'ENTRA_ID' && (
                              <DropdownMenuItem onClick={() => setConfirmAction({ type: 'resetPassword', user: u })}>
                                <RotateCcw className="w-4 h-4 mr-2 text-orange-500" />
                                {t('users.resetPassword')}
                              </DropdownMenuItem>
                            )}
                            {u.authType !== 'ENTRA_ID' && u.totpEnabled && (
                              <DropdownMenuItem onClick={() => setConfirmAction({ type: 'reset2fa', user: u })}>
                                <ShieldOff className="w-4 h-4 mr-2 text-orange-500" />
                                {t('users.reset2fa')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => { setConvertTarget(u); setConvertAuthType(''); }}>
                              <ArrowLeftRight className="w-4 h-4 mr-2 text-blue-500" />
                              {t('users.convertAuth')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setConfirmAction({ type: 'resendWelcome', user: u })}>
                              <MailPlus className="w-4 h-4 mr-2 text-red-500" />
                              {t('users.resendWelcome')}
                            </DropdownMenuItem>
                            {u.id !== currentUser?.id && (
                              <DropdownMenuItem onClick={() => setConfirmAction({ type: 'anonymise', user: u })}>
                                <UserX className="w-4 h-4 mr-2 text-red-500" />
                                {t('users.anonymise')}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {u.id !== currentUser?.id && (
                          <button
                            onClick={() => handleDeleteClick(u)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            title={t('users.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {t('users.showing', { from: page * pageSize + 1, to: Math.min((page + 1) * pageSize, total), total })}
          </p>
          <div className="flex gap-1">
            <Button variant="default" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button variant="default" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Dialog */}
      {dialogOpen && (
        <UserDialog
          editing={editing}
          operators={operators}
          onSave={handleSave}
          onClose={() => setDialogOpen(false)}
        />
      )}

      {/* Delete confirmation / blocked dialog */}
      {deleteTarget && (() => {
        const { user: u, deps } = deleteTarget;
        // Must mirror the server-side gate in admin-users.ts: every RESTRICT relation, not just
        // the first three. A relation missing here shows the user a delete button that 500s.
        const depLines: string[] = [];
        if (deps.createdTickets > 0) depLines.push(`${deps.createdTickets} created ticket(s)`);
        if (deps.assignedTickets > 0) depLines.push(`${deps.assignedTickets} assigned ticket(s)`);
        if (deps.apiKeys > 0) depLines.push(`${deps.apiKeys} API key(s)`);
        if (deps.ticketUpdates > 0) depLines.push(`${deps.ticketUpdates} comment(s) or timeline entr(ies)`);
        if (deps.attachments > 0) depLines.push(`${deps.attachments} uploaded attachment(s)`);
        if (deps.watchers > 0) depLines.push(`${deps.watchers} watched ticket(s)`);
        if (deps.notifications > 0) depLines.push(`${deps.notifications} notification record(s)`);
        const blocked = depLines.length > 0;

        if (blocked) {
          return (
            <ConfirmDialog
              title={t('users.deleteTitle')}
              message={t('users.deleteBlocked', { name: u.displayName })}
              details={depLines}
              confirmLabel={t('users.anonymise')}
              variant="warning"
              onConfirm={() => {
                setDeleteTarget(null);
                setConfirmAction({ type: 'anonymise', user: u });
              }}
              cancelLabel={t('users.deleteBlockedCancel')}
              onCancel={() => setDeleteTarget(null)}
            />
          );
        }

        return (
          <ConfirmDialog
            title={t('users.deleteTitle')}
            message={t('users.deleteConfirm', { name: u.displayName, email: u.email })}
            details={[
              `${t('users.cols.role')}: ${ROLES.find((r) => r.value === u.role)?.label ?? u.role}`,
              `${t('users.cols.operator')}: ${u.operatorName} (${u.operatorCode})`,
            ]}
            confirmLabel={t('users.delete')}
            variant="danger"
            onConfirm={handleDeleteConfirm}
            onCancel={() => setDeleteTarget(null)}
          />
        );
      })()}

      {/* Confirm action dialog (Reset Password / Reset 2FA / Resend Welcome) */}
      {/* Convert auth type dialog */}
      {convertTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="gradient-band px-6 py-4 flex items-center justify-between">
              <h3 className="text-sm font-bold text-white uppercase tracking-wide">{t('users.convertAuthTitle')}</h3>
              <button onClick={() => setConvertTarget(null)} className="text-white/60 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
              <p className="text-sm text-slate-600">
                {t('users.convertAuthDesc', { name: convertTarget.displayName })}
              </p>
              <div className="space-y-2">
                {(['LOCAL', 'ENTRA_ID', 'BOTH'] as const).filter((at) => at !== convertTarget.authType).map((at) => (
                  <label
                    key={at}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      convertAuthType === at ? 'border-brand-rose bg-brand-rose/5' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="convertAuthType"
                      value={at}
                      checked={convertAuthType === at}
                      onChange={() => setConvertAuthType(at)}
                      className="accent-brand-rose"
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-700">
                        {at === 'LOCAL' ? t('users.authLocal') : at === 'ENTRA_ID' ? t('users.authEntra') : t('users.authBoth')}
                      </p>
                      <p className="text-xs text-slate-500">
                        {at === 'LOCAL' ? t('users.authLocalDesc') : at === 'ENTRA_ID' ? t('users.authEntraDesc') : t('users.authBothDesc')}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
              {convertAuthType === 'ENTRA_ID' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                  <p className="text-xs text-amber-700">{t('users.convertWarningToEntra')}</p>
                </div>
              )}
              {convertAuthType === 'LOCAL' && convertTarget.authType === 'ENTRA_ID' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                  <p className="text-xs text-blue-700">{t('users.convertWarningToLocal')}</p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="default" onClick={() => setConvertTarget(null)}>{t('users.cancel')}</Button>
                <Button
                  disabled={!convertAuthType}
                  onClick={handleConvertAuth}
                  className="bg-brand-gradient text-white hover:opacity-90"
                >
                  {t('users.convertConfirm')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (() => {
        const { type, user: u } = confirmAction;
        const configs = {
          resetPassword: {
            title: t('users.resetPasswordTitle'),
            message: t('users.resetPasswordConfirm', { name: u.displayName, email: u.email }),
            confirmLabel: t('users.resetPassword'),
            variant: 'warning' as const,
            handler: () => handleResetPassword(u.id),
          },
          reset2fa: {
            title: t('users.reset2faTitle'),
            message: t('users.reset2faConfirm', { name: u.displayName }),
            confirmLabel: t('users.reset2fa'),
            variant: 'warning' as const,
            handler: () => handleReset2fa(u.id),
          },
          resendWelcome: {
            title: t('users.resendWelcomeTitle'),
            message: t('users.resendWelcomeConfirm', { name: u.displayName, email: u.email }),
            details: [t('users.resendWelcomeWarning')],
            confirmLabel: t('users.resendWelcome'),
            variant: 'danger' as const,
            handler: () => handleResendWelcome(u.id),
          },
          anonymise: {
            title: t('users.anonymiseTitle'),
            message: t('users.anonymiseConfirm', { name: u.displayName, email: u.email }),
            details: [
              t('users.anonymiseDetailClears'),
              t('users.anonymiseDetailKeeps'),
              t('users.anonymiseDetailIrreversible'),
            ],
            confirmLabel: t('users.anonymise'),
            variant: 'danger' as const,
            handler: () => handleAnonymise(u.id),
          },
        };
        const cfg = configs[type];
        return (
          <ConfirmDialog
            title={cfg.title}
            message={cfg.message}
            details={'details' in cfg ? cfg.details : undefined}
            confirmLabel={cfg.confirmLabel}
            variant={cfg.variant}
            onConfirm={cfg.handler}
            onCancel={() => setConfirmAction(null)}
          />
        );
      })()}
    </div>
  );
}

// ── User Dialog ─────────────────────────────────────────────

interface UserDialogProps {
  editing: AdminUserItem | null;
  operators: OperatorListItem[];
  onSave: (data: CreateUserRequest | UpdateUserRequest) => Promise<void>;
  onClose: () => void;
}

function UserDialog({ editing, operators, onSave, onClose }: UserDialogProps) {
  const { t } = useTranslation('admin');
  const [email, setEmail] = useState(editing?.email || '');
  const [displayName, setDisplayName] = useState(editing?.displayName || '');
  const [role, setRole] = useState(editing?.role || '');
  const [operatorId, setOperatorId] = useState(editing?.operatorId || '');
  const [authType, setAuthType] = useState<'LOCAL' | 'ENTRA_ID' | 'BOTH'>(
    (editing?.authType as 'LOCAL' | 'ENTRA_ID' | 'BOTH') || 'BOTH',
  );
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [saving, setSaving] = useState(false);

  // Group selection
  const [availableGroups, setAvailableGroups] = useState<GroupAdminItem[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(
    editing?.groups?.map((g) => g.id) ?? [],
  );
  const [loadingGroups, setLoadingGroups] = useState(false);

  useEffect(() => {
    if (!operatorId) { setAvailableGroups([]); return; }
    let cancelled = false;
    setLoadingGroups(true);
    fetchAdminGroups({ operatorId, active: 'true' })
      .then((res) => {
        if (cancelled) return;
        setAvailableGroups(res.data);
        // Pre-select default group for new users
        if (!editing) {
          const def = res.data.find((g) => g.isDefault);
          setSelectedGroupIds(def ? [def.id] : []);
        }
      })
      .catch(() => { if (!cancelled) setAvailableGroups([]); })
      .finally(() => { if (!cancelled) setLoadingGroups(false); });
    return () => { cancelled = true; };
  }, [operatorId, editing]);

  function toggleGroup(id: string) {
    setSelectedGroupIds((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id],
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (selectedGroupIds.length === 0 && availableGroups.length > 0) {
      toast.error(t('users.groupRequired'));
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await onSave({
          displayName,
          role,
          operatorId,
          isActive,
          groupIds: selectedGroupIds,
        } satisfies UpdateUserRequest);
      } else {
        if (!email || !displayName || !role || !operatorId) {
          toast.error(t('users.allFieldsRequired'));
          return;
        }
        await onSave({
          email,
          displayName,
          role,
          operatorId,
          authType,
          groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
        } satisfies CreateUserRequest);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="gradient-band px-6 py-4 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">
            {editing ? t('users.editTitle') : t('users.createTitle')}
          </h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[calc(100vh-8rem)] overflow-y-auto">
          {/* Email (create only) */}
          {!editing && (
            <div className="space-y-1.5">
              <Label>{t('users.fields.email')}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={255} />
            </div>
          )}

          {/* Display name */}
          <div className="space-y-1.5">
            <Label>{t('users.fields.displayName')}</Label>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required maxLength={255} />
          </div>

          {/* Role */}
          <div className="space-y-1.5">
            <Label>{t('users.fields.role')}</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue placeholder={t('users.fields.selectRole')} />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Operator */}
          <div className="space-y-1.5">
            <Label>{t('users.fields.operator')}</Label>
            <Select value={operatorId} onValueChange={setOperatorId}>
              <SelectTrigger>
                <SelectValue placeholder={t('users.fields.selectOperator')} />
              </SelectTrigger>
              <SelectContent>
                {operators.map((op) => <SelectItem key={op.id} value={op.id}>{op.name} ({op.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Groups */}
          {operatorId && (
            <div className="space-y-1.5">
              <Label>{t('users.fields.groups')}</Label>
              {loadingGroups ? (
                <div className="text-xs text-slate-400 py-2">{t('users.saving')}</div>
              ) : availableGroups.length === 0 ? (
                <div className="text-xs text-slate-400 py-2">{t('users.noGroupsForOperator')}</div>
              ) : (
                <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                  {availableGroups.map((g) => (
                    <label
                      key={g.id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedGroupIds.includes(g.id) ? 'bg-brand-rose/5' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedGroupIds.includes(g.id)}
                        onChange={() => toggleGroup(g.id)}
                        className="rounded border-slate-300 text-brand-rose focus:ring-brand-rose/30"
                      />
                      <span className="text-sm text-slate-700">{g.name}</span>
                      {g.isDefault && (
                        <Badge variant="default" className="text-[10px] ml-auto">{t('users.defaultGroup')}</Badge>
                      )}
                    </label>
                  ))}
                </div>
              )}
              {selectedGroupIds.length === 0 && availableGroups.length > 0 && (
                <p className="text-xs text-red-500">{t('users.groupRequired')}</p>
              )}
            </div>
          )}

          {/* Auth type (create only) */}
          {!editing && (
            <div className="space-y-1.5">
              <Label>{t('users.authType')}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {([
                  { value: 'BOTH', label: t('users.authBoth'), desc: t('users.authBothDesc') },
                  { value: 'LOCAL', label: t('users.authLocal'), desc: t('users.authLocalDesc') },
                  { value: 'ENTRA_ID', label: t('users.authEntra'), desc: t('users.authEntraDesc') },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAuthType(opt.value)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      authType === opt.value ? 'border-brand-rose bg-brand-rose/5 ring-1 ring-brand-rose/30' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="text-xs font-semibold text-slate-700">{opt.label}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Active toggle (edit only) */}
          {editing && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIsActive(!isActive)}
                className={`relative w-11 h-6 rounded-full transition-colors ${isActive ? 'bg-green-500' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isActive ? 'translate-x-5' : ''}`} />
              </button>
              <Label className="cursor-pointer" onClick={() => setIsActive(!isActive)}>
                {isActive ? t('users.active') : t('users.inactive')}
              </Label>
            </div>
          )}

          {/* Info note (create only) */}
          {!editing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
              <p className="text-xs text-blue-700">
                {authType === 'LOCAL' && t('users.createNote')}
                {authType === 'ENTRA_ID' && t('users.createNoteEntra')}
                {authType === 'BOTH' && t('users.createNoteBoth')}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="default" onClick={onClose}>{t('users.cancel')}</Button>
            <Button type="submit" disabled={saving} className="bg-brand-gradient text-white hover:opacity-90">
              {saving ? t('users.saving') : editing ? t('users.update') : t('users.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
